import type { LucideIcon } from "lucide-react";

export type Chip<T extends string> = { valor: T; rotulo: string; icone?: LucideIcon };

/**
 * Fila de chips: filtros de categoria, abas curtas.
 *
 * Referência: a fila "Dinner / Supper / Snack" do Dribbble 15125197, com o
 * ativo preenchido de dourado. Aqui o ativo é latão sobre texto de latão
 * (--accent / --accent-foreground) e os demais são cartão com borda. Altura
 * mínima 56px — chip de 32px é o que esse público erra.
 *
 * `quebrar`: as fichas descem para a linha de baixo em vez de rolar para o
 * lado. Use quando a lista é longa ou é a navegação da tela (as categorias
 * de um produto): numa fita com a barra de rolagem escondida, quem não sabe
 * que dá para arrastar vê quatro fichas e acha que são todas.
 */
export function Chips<T extends string>({
  itens,
  valor,
  onEscolher,
  quebrar = false,
}: {
  itens: Chip<T>[];
  valor: T | null;
  onEscolher: (v: T) => void;
  quebrar?: boolean;
}) {
  return (
    <div
      className={
        quebrar
          ? "flex flex-wrap"
          : "flex overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      }
      style={{ gap: 10, paddingLeft: "var(--gutter)", paddingRight: "var(--gutter)" }}
      role="tablist"
    >
      {itens.map((c) => {
        const ativo = c.valor === valor;
        const Icone = c.icone;
        return (
          <button
            key={c.valor}
            type="button"
            role="tab"
            aria-selected={ativo}
            onClick={() => onEscolher(c.valor)}
            className={`toque flex shrink-0 items-center gap-2 rounded-full border px-5 font-semibold ${
              ativo ? "border-transparent" : "border-border bg-card"
            }`}
            style={{
              minHeight: "var(--alvo-min)",
              fontSize: "var(--t-apoio)",
              backgroundColor: ativo ? "hsl(var(--accent))" : undefined,
              color: ativo ? "hsl(var(--accent-foreground))" : "var(--tinta)",
              transition: "background-color 180ms, color 180ms, transform 140ms",
            }}
          >
            {Icone && <Icone className="h-5 w-5" strokeWidth={2.2} />}
            {c.rotulo}
          </button>
        );
      })}
    </div>
  );
}
